/**
 * Page de bascule de spécialisation pour les étudiants L3DI&GL.
 *
 * Au S5, ils étaient ensemble (matières identiques). Au S6, ils se séparent en
 * DI ou GL. Cette page permet à la scolarité de basculer chaque étudiant :
 *   - Les matières S5 sont dupliquées sous l'option DI et GL (idempotent)
 *   - Les notes et résultats de l'étudiant sont ré-attachés à la spécialisation choisie
 *   - L'étudiant passe en classe L3DI ou L3GL
 *
 * L'opération est réversible : on peut re-basculer un étudiant de DI vers GL et inversement.
 */
import { useMemo, useState } from 'react';
import { Split, Loader2, ArrowRight, CheckCircle, AlertTriangle, RotateCw, Search, GraduationCap } from 'lucide-react';
import {
  useListerL3DIGLABasculerQuery,
  useBasculerSpecialisationMutation,
} from '../../services/etudiantsApi';

interface EtuRow {
  id: number;
  matricule: string;
  nom: string;
  prenoms: string;
  classe: string;
  option: string;
  photo_url: string | null;
}

const META: Record<string, { libelle: string; couleur: string; bg: string }> = {
  'L3DI&GL': { libelle: 'À basculer',     couleur: 'text-amber-700 dark:text-amber-300', bg: 'bg-amber-100 dark:bg-amber-900/40' },
  'L3DI':    { libelle: 'DI',             couleur: 'text-blue-700 dark:text-blue-300',   bg: 'bg-blue-100 dark:bg-blue-900/40' },
  'L3GL':    { libelle: 'GL',             couleur: 'text-purple-700 dark:text-purple-300',bg: 'bg-purple-100 dark:bg-purple-900/40' },
};

export default function BasculeSpecialisation() {
  const { data, isFetching, refetch } = useListerL3DIGLABasculerQuery();
  const [basculer, { isLoading: basculing }] = useBasculerSpecialisationMutation();
  const [search, setSearch] = useState('');
  const [enCours, setEnCours] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; texte: string } | null>(null);

  const etudiants = data?.results || [];

  const filtres = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return etudiants;
    return etudiants.filter(e =>
      e.matricule.toLowerCase().includes(q)
      || e.nom.toLowerCase().includes(q)
      || e.prenoms.toLowerCase().includes(q)
    );
  }, [etudiants, search]);

  const stats = useMemo(() => {
    const out = { a_basculer: 0, di: 0, gl: 0 };
    for (const e of etudiants) {
      if (e.classe === 'L3DI&GL') out.a_basculer++;
      else if (e.classe === 'L3DI') out.di++;
      else if (e.classe === 'L3GL') out.gl++;
    }
    return out;
  }, [etudiants]);

  const handleBasculer = async (etu: EtuRow, specialisation: 'DI' | 'GL') => {
    const dejaPareil = etu.classe === `L3${specialisation}`;
    if (dejaPareil) return;

    const action = etu.classe === 'L3DI&GL' ? 'basculer' : 're-basculer';
    if (!window.confirm(
      `${action.toUpperCase()} l'étudiant ${etu.nom} ${etu.prenoms} (${etu.matricule}) vers L3${specialisation} ?\n\n`
      + `Cette opération :\n`
      + `• Dupliquera les matières S5 si pas encore fait\n`
      + `• Ré-attachera ses notes et résultats à la spécialisation ${specialisation}\n`
      + `• Changera sa classe en L3${specialisation}\n\n`
      + `Cette opération est réversible (vous pourrez la re-basculer plus tard).`
    )) return;

    setEnCours(etu.id);
    setMessage(null);
    try {
      const res = await basculer({ id: etu.id, specialisation }).unwrap();
      const m = res.migration;
      setMessage({
        type: 'success',
        texte: `${etu.nom} ${etu.prenoms} → L3${specialisation} : `
          + `${m.notes_devoirs_reattachees} notes devoirs + ${m.notes_examens_reattachees} notes examens + ${m.resultats_ecue_reattaches} résultats ECUE ré-attachés.`,
      });
      refetch();
    } catch (e: any) {
      setMessage({
        type: 'error',
        texte: e?.data?.error || e?.message || 'Erreur lors de la bascule',
      });
    } finally {
      setEnCours(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <header className="mb-5">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Split size={22} className="text-blue-600 dark:text-blue-400" />
          Bascule de spécialisation — L3DI&amp;GL
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1.5 max-w-3xl">
          Au S5, tous les étudiants L3DI&amp;GL suivaient les mêmes matières.
          Au S6, ils se séparent en <strong>L3DI</strong> (Développement Informatique) ou <strong>L3GL</strong> (Génie Logiciel).
          Cette page permet de basculer chaque étudiant vers sa spécialisation.
          Les notes du S5 sont conservées et ré-attachées automatiquement.
        </p>
      </header>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <KPI label="À basculer" valeur={stats.a_basculer} couleur="amber" />
        <KPI label="Basculés DI" valeur={stats.di} couleur="blue" />
        <KPI label="Basculés GL" valeur={stats.gl} couleur="purple" />
      </div>

      {/* Recherche + refresh */}
      <div className="flex flex-wrap gap-2 mb-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par matricule, nom ou prénom…"
            className="w-full pl-9 pr-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <button onClick={() => refetch()} disabled={isFetching}
          className="px-3 py-2 text-xs bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg inline-flex items-center gap-1.5">
          <RotateCw size={13} className={isFetching ? 'animate-spin' : ''} /> Actualiser
        </button>
      </div>

      {/* Message d'état */}
      {message && (
        <div className={`mb-3 px-3 py-2 rounded-lg text-sm flex items-start gap-2 ${
          message.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
        }`}>
          {message.type === 'success' ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
          <span className="flex-1">{message.texte}</span>
          <button onClick={() => setMessage(null)} className="opacity-70 hover:opacity-100">×</button>
        </div>
      )}

      {/* Liste */}
      {isFetching && etudiants.length === 0 && (
        <div className="text-center py-10 text-sm text-gray-500">Chargement…</div>
      )}

      {!isFetching && etudiants.length === 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-8 text-center">
          <GraduationCap size={36} strokeWidth={1.3} className="mx-auto mb-3 text-gray-400" />
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Aucun étudiant L3DI&amp;GL trouvé.</div>
          <p className="text-xs text-gray-500 mt-1">Vérifiez que les étudiants L3DI&amp;GL sont bien actifs en base.</p>
        </div>
      )}

      {filtres.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Étudiant</th>
                <th className="px-3 py-2 text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Statut actuel</th>
                <th className="px-3 py-2 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
              {filtres.map(etu => {
                const meta = META[etu.classe] || META['L3DI&GL'];
                const isLoading = enCours === etu.id;
                return (
                  <tr key={etu.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 overflow-hidden">
                          {etu.photo_url ? (
                            <img src={etu.photo_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            (etu.prenoms[0] || '') + (etu.nom[0] || '')
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                            {etu.nom} {etu.prenoms}
                          </div>
                          <div className="text-[11px] text-gray-500 font-mono">{etu.matricule}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <span className={`inline-block px-2 py-0.5 text-[11px] font-semibold rounded ${meta.bg} ${meta.couleur}`}>
                        {etu.classe}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex justify-end gap-1.5">
                        <button onClick={() => handleBasculer(etu, 'DI')}
                          disabled={isLoading || basculing || etu.classe === 'L3DI'}
                          className={`px-2.5 py-1.5 text-xs font-semibold rounded inline-flex items-center gap-1 ${
                            etu.classe === 'L3DI'
                              ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-400 dark:text-blue-300 cursor-default'
                              : 'bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50'
                          }`}>
                          {isLoading ? <Loader2 size={12} className="animate-spin" /> : <ArrowRight size={11} />} L3DI
                        </button>
                        <button onClick={() => handleBasculer(etu, 'GL')}
                          disabled={isLoading || basculing || etu.classe === 'L3GL'}
                          className={`px-2.5 py-1.5 text-xs font-semibold rounded inline-flex items-center gap-1 ${
                            etu.classe === 'L3GL'
                              ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-400 dark:text-purple-300 cursor-default'
                              : 'bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50'
                          }`}>
                          {isLoading ? <Loader2 size={12} className="animate-spin" /> : <ArrowRight size={11} />} L3GL
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function KPI({ label, valeur, couleur }: { label: string; valeur: number; couleur: 'amber' | 'blue' | 'purple' }) {
  const map = {
    amber:  'bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300',
    blue:   'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300',
    purple: 'bg-purple-50 dark:bg-purple-900/30 border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-300',
  };
  return (
    <div className={`px-4 py-3 rounded-lg border ${map[couleur]}`}>
      <div className="text-2xl font-bold">{valeur}</div>
      <div className="text-xs font-medium opacity-80">{label}</div>
    </div>
  );
}
